import React, { useState, useEffect, useMemo } from 'react';
import {
  DollarSign, BarChart3, Code2, ExternalLink, Plus, Trash2, Check, Copy,
  AlertTriangle, RefreshCw, Eye, History, ShieldCheck, ChevronDown, ChevronRight,
  Calendar, Sparkles, Edit3, Save, Globe, Target
} from 'lucide-react';
import { auth } from '../firebase';

const TRAFFIC_SOURCE_OPTIONS = [
  { id: 'mgid', label: 'MGID', macro: '{click_id}' },
  { id: 'taboola', label: 'Taboola', macro: '{click_id}' },
  { id: 'outbrain', label: 'Outbrain', macro: '{{ob_click_id}}' },
  { id: 'google', label: 'Google Ads', macro: '{gclid}' },
  { id: 'facebook', label: 'Facebook Ads', macro: '{{fbclid}}' },
  { id: 'tiktok', label: 'TikTok Ads', macro: '__CLICKID__' },
  { id: 'youtube', label: 'YouTube Ads', macro: '{gclid}' },
  { id: 'email', label: 'Email / Solo Ads', macro: '{subid}' },
  { id: 'organic', label: 'Organic / SEO', macro: '' },
];

export default function PaidTrafficManager({ campaigns = [], initialTab = 'report', selectedCampaignId = '', onCampaignUpdated }) {
  const [activeTab, setActiveTab] = useState(initialTab); // 'report' | 'spend' | 'custom-html' | 'external-pages'
  const [copiedKey, setCopiedKey] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const copyText = (text, key) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  const getAuthHeaders = async () => {
    const user = auth.currentUser;
    const token = user ? await user.getIdToken() : '';
    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // STEP 8: PAID REPORT STATE
  // ═══════════════════════════════════════════════════════════════════════════
  const formatDateLA = (d) => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Los_Angeles', year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
  const todayStr = formatDateLA(new Date());
  const thirtyDaysAgoStr = formatDateLA(new Date(Date.now() - 30 * 86400000));
  const [startDate, setStartDate] = useState(thirtyDaysAgoStr);
  const [endDate, setEndDate] = useState(todayStr);
  const [reportFilterCampaign, setReportFilterCampaign] = useState('');
  const [reportFilterSource, setReportFilterSource] = useState('');
  const [reportData, setReportData] = useState({ totals: null, campaigns: [] });
  const [loadingReport, setLoadingReport] = useState(false);
  const [expandedCampaigns, setExpandedCampaigns] = useState({});
  const [expandedAngles, setExpandedAngles] = useState({});
  const [expandedPages, setExpandedPages] = useState({});

  const loadPaidReport = async () => {
    setLoadingReport(true);
    try {
      const headers = await getAuthHeaders();
      const params = new URLSearchParams();
      if (startDate) params.set('startDate', startDate);
      if (endDate) params.set('endDate', endDate);
      if (reportFilterCampaign) params.set('campaignId', reportFilterCampaign);
      if (reportFilterSource) params.set('source', reportFilterSource);

      const res = await fetch(`/api/reports/paid?${params.toString()}`, { headers });
      if (res.ok) {
        const data = await res.json();
        setReportData(data);
        // Auto-expand campaigns with activity
        const expC = {};
        (data.campaigns || []).forEach(c => { expC[c.campaignId] = true; });
        setExpandedCampaigns(expC);
      }
    } catch (err) {
      console.error('Failed to load paid report:', err);
    } finally {
      setLoadingReport(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // STEP 7: DAILY SPEND STATE
  // ═══════════════════════════════════════════════════════════════════════════
  const [spendRows, setSpendRows] = useState([]);
  const [spendForm, setSpendForm] = useState({
    date: todayStr,
    source: 'mgid',
    campaignId: selectedCampaignId || (campaigns[0]?.id || ''),
    amount: '',
  });
  const [savingSpend, setSavingSpend] = useState(false);

  const loadSpendRows = async () => {
    try {
      const headers = await getAuthHeaders();
      const res = await fetch('/api/spend', { headers });
      if (res.ok) {
        const data = await res.json();
        setSpendRows(data.rows || []);
      }
    } catch (err) {
      console.error('Failed to load spend rows:', err);
    }
  };

  const handleSaveSpend = async (e) => {
    e.preventDefault();
    if (!spendForm.campaignId || !spendForm.date || spendForm.amount === '') {
      showToast('Please select a date, campaign, and amount.', 'error');
      return;
    }
    setSavingSpend(true);
    try {
      const headers = await getAuthHeaders();
      const campObj = campaigns.find(c => c.id === spendForm.campaignId);
      const res = await fetch('/api/spend', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          date: spendForm.date,
          source: spendForm.source,
          campaign: spendForm.campaignId,
          campaignId: spendForm.campaignId,
          campaignName: campObj?.name || spendForm.campaignId,
          amount: Number(spendForm.amount),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save spend');
      showToast(`Saved $${Number(spendForm.amount).toFixed(2)} spend for ${campObj?.name || spendForm.campaignId} on ${spendForm.date}`);
      setSpendForm(prev => ({ ...prev, amount: '' }));
      loadSpendRows();
      loadPaidReport();
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setSavingSpend(false);
    }
  };

  const handleEditSpendRow = (row) => {
    setSpendForm({
      date: row.date,
      source: row.source || 'mgid',
      campaignId: row.campaignId || row.campaign,
      amount: String(row.amount),
    });
  };

  const handleDeleteSpendRow = async (id) => {
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`/api/spend/${id}`, { method: 'DELETE', headers });
      if (res.ok) {
        setSpendRows(prev => prev.filter(r => r.id !== id));
        showToast('Spend entry deleted');
        loadPaidReport();
      }
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // STEP 5: CUSTOM HTML PAGE EDITOR STATE
  // ═══════════════════════════════════════════════════════════════════════════
  const [htmlCampaignId, setHtmlCampaignId] = useState(selectedCampaignId || (campaigns[0]?.id || ''));
  const [htmlNodeId, setHtmlNodeId] = useState('custom-html-1');
  const [htmlPageSlug, setHtmlPageSlug] = useState('');
  const [htmlAngleId, setHtmlAngleId] = useState('angle-1');
  const [htmlCode, setHtmlCode] = useState(`<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>Special Editorial Report</title>
  <style>
    body { font-family: system-ui, -apple-system, sans-serif; max-width: 760px; margin: 40px auto; padding: 0 20px; line-height: 1.6; color: #1e293b; }
    .cta-btn { display: inline-block; background: #16a34a; color: #fff; padding: 14px 28px; border-radius: 8px; text-decoration: none; font-weight: 700; margin: 12px 8px 12px 0; }
  </style>
</head>
<body>
  <h1>Breaking Consumer Report</h1>
  <p>Compare the top-rated options below:</p>
  <a href="{{offer:1}}" class="cta-btn">Claim Offer #1 (Primary) →</a>
  <a href="{{offer:2}}" class="cta-btn" style="background:#2563eb;">Check Offer #2 (Alternative) →</a>
</body>
</html>`);
  const [htmlNoindex, setHtmlNoindex] = useState(true);
  const [htmlSlots, setHtmlSlots] = useState([
    { slot: '1', offer: '', slug: '' },
    { slot: '2', offer: '', slug: '' },
  ]);
  const [htmlVersions, setHtmlVersions] = useState([]);
  const [htmlWarnings, setHtmlWarnings] = useState([]);
  const [savingHtml, setSavingHtml] = useState(false);
  const [showHtmlPreview, setShowHtmlPreview] = useState(true);

  const htmlByteSize = useMemo(() => new Blob([htmlCode || '']).size, [htmlCode]);
  const hasLocalOfferPlaceholders = useMemo(() => {
    return /\{\{\s*offer\s*:\s*(\d+)\s*\}\}/i.test(htmlCode) || /data-afl-slot\s*=\s*["'](\d+)["']/i.test(htmlCode);
  }, [htmlCode]);

  const loadCustomHtmlNode = async (campId, nodeId) => {
    if (!campId || !nodeId) return;
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`/api/campaigns/${campId}/custom-html/${nodeId}`, { headers });
      if (res.ok) {
        const data = await res.json();
        if (data.html) setHtmlCode(data.html);
        setHtmlNoindex(data.noindex !== false);
        setHtmlVersions(Array.isArray(data.versions) ? data.versions : []);
        if (Array.isArray(data.slots) && data.slots.length > 0) setHtmlSlots(data.slots);
        if (data.pageSlug) setHtmlPageSlug(data.pageSlug);
      }
    } catch (err) {
      // Node may not exist yet, keep starter template
    }
  };

  useEffect(() => {
    if (htmlCampaignId && htmlNodeId) {
      loadCustomHtmlNode(htmlCampaignId, htmlNodeId);
    }
  }, [htmlCampaignId, htmlNodeId]);

  const handleSaveCustomHtml = async () => {
    if (!htmlCampaignId) {
      showToast('Select a campaign first', 'error');
      return;
    }
    if (htmlByteSize > 1048576) {
      showToast('Custom HTML exceeds 1 MB limit', 'error');
      return;
    }
    setSavingHtml(true);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`/api/campaigns/${htmlCampaignId}/custom-html`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          nodeId: htmlNodeId,
          html: htmlCode,
          noindex: htmlNoindex,
          slots: htmlSlots,
          pageSlug: htmlPageSlug || undefined,
          angleId: htmlAngleId || 'angle-1',
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save Custom HTML');
      setHtmlVersions(data.versions || []);
      setHtmlWarnings(data.warnings || []);
      showToast(data.hasOfferPlaceholders
        ? `Custom HTML saved (${data.placeholderSlotsFound.length} offer slot(s) active)`
        : 'Saved with warning: No {{offer:N}} placeholders found!');
      if (onCampaignUpdated) onCampaignUpdated();
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setSavingHtml(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // STEP 6: EXTERNAL PAGES (OPTION B) STATE
  // ═══════════════════════════════════════════════════════════════════════════
  const [externalPages, setExternalPages] = useState([]);
  const [selectedExtPage, setSelectedExtPage] = useState(null);
  const [extForm, setExtForm] = useState({
    name: '',
    url: 'https://moneyxps.com/',
    campaignId: selectedCampaignId || (campaigns[0]?.id || ''),
    angle: 'angle-1',
    source: 'mgid',
    customDomain: 'go.moneyxps.com',
    slots: [
      { slot: '1', offer: '', slug: '' },
      { slot: '2', offer: '', slug: '' },
    ],
  });
  const [savingExtPage, setSavingExtPage] = useState(false);

  const loadExternalPages = async () => {
    try {
      const headers = await getAuthHeaders();
      const res = await fetch('/api/external-pages', { headers });
      if (res.ok) {
        const data = await res.json();
        setExternalPages(data.pages || []);
        if (data.pages?.length > 0 && !selectedExtPage) {
          setSelectedExtPage(data.pages[0]);
        }
      }
    } catch (err) {
      console.error('Failed to load external pages:', err);
    }
  };

  const handleSaveExternalPage = async (e) => {
    e.preventDefault();
    if (!extForm.url) {
      showToast('External page URL is required', 'error');
      return;
    }
    setSavingExtPage(true);
    try {
      const headers = await getAuthHeaders();
      const campObj = campaigns.find(c => c.id === extForm.campaignId);
      const res = await fetch('/api/external-pages', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ...extForm,
          campaign: extForm.campaignId || campObj?.slug || campObj?.name || 'default',
          campaignName: campObj?.name || '',
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to register external page');
      showToast(`Registered external page: ${data.page.name}`);
      setSelectedExtPage(data.page);
      loadExternalPages();
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setSavingExtPage(false);
    }
  };

  const handleDeleteExternalPage = async (id) => {
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`/api/external-pages/${id}`, { method: 'DELETE', headers });
      if (res.ok) {
        setExternalPages(prev => prev.filter(p => p.id !== id));
        if (selectedExtPage?.id === id) setSelectedExtPage(null);
        showToast('External page removed');
      }
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  useEffect(() => {
    loadPaidReport();
    loadSpendRows();
    loadExternalPages();
  }, []);

  useEffect(() => {
    if (selectedCampaignId) {
      setSpendForm(p => ({ ...p, campaignId: selectedCampaignId }));
      setHtmlCampaignId(selectedCampaignId);
      setExtForm(p => ({ ...p, campaignId: selectedCampaignId }));
    }
  }, [selectedCampaignId]);

  const renderEpcPctBadge = (pct, color) => {
    if (pct === null || pct === undefined) {
      return <span className="text-slate-500 font-mono text-xs">—</span>;
    }
    const styles = color === 'green'
      ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30'
      : color === 'yellow'
        ? 'bg-amber-500/15 text-amber-300 border-amber-500/30'
        : 'bg-rose-500/15 text-rose-400 border-rose-500/30';
    return (
      <span className={`px-2 py-0.5 rounded-full text-[11px] font-mono font-black border ${styles}`}>
        {Number(pct).toFixed(1)}%
      </span>
    );
  };

  const totals = reportData.totals || {
    spend: 0, visits: 0, quizCompletionRate: 0, slotClicks: 0, ctr: 0,
    conversions: 0, revenue: 0, profit: 0, cpc: 0, epc: 0, roi: null, epcPctOfCpc: null, epcVsCpcColor: 'neutral'
  };

  const selectedHtmlCampaign = campaigns.find(c => c.id === htmlCampaignId);
  const activeCustomDomain = (selectedHtmlCampaign?.customDomain && selectedHtmlCampaign.customDomain !== 'xpgo.link')
    ? selectedHtmlCampaign.customDomain
    : 'go.moneyxps.com';
  const effectiveHtmlSlug = htmlPageSlug || selectedHtmlCampaign?.anglePlans?.[0]?.pageSlug || selectedHtmlCampaign?.slug || 'custom-landing';
  const customHtmlLiveUrl = `https://${activeCustomDomain}/${effectiveHtmlSlug}`;

  return (
    <div className="space-y-6 text-slate-200">
      {/* Toast Notification */}
      {toast && (
        <div className={`fixed bottom-6 right-6 z-50 px-5 py-3 rounded-2xl border shadow-2xl flex items-center gap-2.5 text-xs font-bold ${
          toast.type === 'error'
            ? 'bg-rose-950/95 border-rose-500/40 text-rose-200'
            : 'bg-emerald-950/95 border-emerald-500/40 text-emerald-200'
        }`}>
          {toast.type === 'error' ? <AlertTriangle size={15} className="text-rose-400" /> : <Check size={15} className="text-emerald-400" />}
          <span>{toast.message}</span>
        </div>
      )}

      {/* Navigation Sub-Tabs */}
      <div className="flex flex-wrap items-center justify-between gap-4 bg-[#11151F] border border-slate-800/80 p-2 rounded-2xl">
        <div className="flex flex-wrap items-center gap-1.5">
          <button
            onClick={() => setActiveTab('report')}
            className={`px-4 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider flex items-center gap-2 transition-all cursor-pointer ${
              activeTab === 'report' ? 'bg-[#02B6FF] text-slate-950 shadow-lg' : 'text-slate-400 hover:text-white hover:bg-slate-800/60'
            }`}
          >
            <BarChart3 size={15} />
            <span>Paid Report & P&amp;L</span>
          </button>
          <button
            onClick={() => setActiveTab('spend')}
            className={`px-4 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider flex items-center gap-2 transition-all cursor-pointer ${
              activeTab === 'spend' ? 'bg-[#02B6FF] text-slate-950 shadow-lg' : 'text-slate-400 hover:text-white hover:bg-slate-800/60'
            }`}
          >
            <DollarSign size={15} />
            <span>Daily Spend Entry</span>
          </button>
          <button
            onClick={() => setActiveTab('custom-html')}
            className={`px-4 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider flex items-center gap-2 transition-all cursor-pointer ${
              activeTab === 'custom-html' ? 'bg-[#02B6FF] text-slate-950 shadow-lg' : 'text-slate-400 hover:text-white hover:bg-slate-800/60'
            }`}
          >
            <Code2 size={15} />
            <span>Option A: Custom HTML Page</span>
          </button>
          <button
            onClick={() => setActiveTab('external-pages')}
            className={`px-4 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider flex items-center gap-2 transition-all cursor-pointer ${
              activeTab === 'external-pages' ? 'bg-[#02B6FF] text-slate-950 shadow-lg' : 'text-slate-400 hover:text-white hover:bg-slate-800/60'
            }`}
          >
            <ExternalLink size={15} />
            <span>Option B: External Pages (t.js)</span>
          </button>
        </div>

        <button
          onClick={() => { loadPaidReport(); loadSpendRows(); loadExternalPages(); }}
          className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl text-xs font-bold flex items-center gap-1.5 cursor-pointer"
        >
          <RefreshCw size={13} className={loadingReport ? 'animate-spin' : ''} />
          <span>Refresh</span>
        </button>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════
          TAB 1: STEP 8 — PAID REPORT & P&L BAR
      ═══════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'report' && (
        <div className="space-y-6">
          {/* Date & Source Filter Bar */}
          <div className="bg-[#11151F] border border-slate-800 rounded-2xl p-4 flex flex-wrap items-end justify-between gap-4">
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label className="block text-[10px] font-black uppercase tracking-wider text-slate-400 mb-1">Start Date</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={e => setStartDate(e.target.value)}
                  className="bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs text-white font-mono"
                />
              </div>
              <div>
                <label className="block text-[10px] font-black uppercase tracking-wider text-slate-400 mb-1">End Date</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={e => setEndDate(e.target.value)}
                  className="bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs text-white font-mono"
                />
              </div>
              <div>
                <label className="block text-[10px] font-black uppercase tracking-wider text-slate-400 mb-1">Campaign</label>
                <select
                  value={reportFilterCampaign}
                  onChange={e => setReportFilterCampaign(e.target.value)}
                  className="bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs text-white font-bold"
                >
                  <option value="">All Campaigns</option>
                  {campaigns.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-black uppercase tracking-wider text-slate-400 mb-1">Traffic Source</label>
                <select
                  value={reportFilterSource}
                  onChange={e => setReportFilterSource(e.target.value)}
                  className="bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs text-white font-bold"
                >
                  <option value="">All Sources</option>
                  {TRAFFIC_SOURCE_OPTIONS.map(s => (
                    <option key={s.id} value={s.id}>{s.label}</option>
                  ))}
                </select>
              </div>
              <button
                onClick={loadPaidReport}
                className="px-4 py-2 bg-[#02B6FF] hover:bg-[#33D5E6] text-slate-950 font-black text-xs rounded-xl uppercase tracking-wider cursor-pointer"
              >
                Apply Filter
              </button>
            </div>

            <div className="flex items-center gap-2 text-[11px] text-slate-400">
              <span className="px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 font-mono">Green ≥ 60%</span>
              <span className="px-2 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30 font-mono">Yellow 30–60%</span>
              <span className="px-2 py-0.5 rounded bg-rose-500/15 text-rose-400 border border-rose-500/30 font-mono">Red &lt; 30%</span>
              <span className="text-slate-500">(EPC % of CPC · Bots Excluded)</span>
            </div>
          </div>

          {/* P&L Totals Bar */}
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-10 gap-3 bg-slate-900/90 border border-slate-800 rounded-2xl p-4 shadow-xl">
            <div className="p-2.5 bg-slate-950/80 rounded-xl border border-slate-800/80">
              <div className="text-[10px] font-black uppercase text-slate-500">Spend</div>
              <div className="text-base font-black font-mono text-rose-400 mt-0.5">${Number(totals.spend || 0).toFixed(2)}</div>
            </div>
            <div className="p-2.5 bg-slate-950/80 rounded-xl border border-slate-800/80">
              <div className="text-[10px] font-black uppercase text-slate-500">Visits</div>
              <div className="text-base font-black font-mono text-white mt-0.5">{totals.visits || 0}</div>
            </div>
            <div className="p-2.5 bg-slate-950/80 rounded-xl border border-slate-800/80">
              <div className="text-[10px] font-black uppercase text-slate-500">Quiz %</div>
              <div className="text-base font-black font-mono text-indigo-300 mt-0.5">{Number(totals.quizCompletionRate || 0).toFixed(1)}%</div>
            </div>
            <div className="p-2.5 bg-slate-950/80 rounded-xl border border-slate-800/80">
              <div className="text-[10px] font-black uppercase text-slate-500">Slot Clicks / CTR</div>
              <div className="text-base font-black font-mono text-cyan-300 mt-0.5">
                {totals.slotClicks || 0} <span className="text-xs text-slate-400">({Number(totals.ctr || 0).toFixed(1)}%)</span>
              </div>
            </div>
            <div className="p-2.5 bg-slate-950/80 rounded-xl border border-slate-800/80">
              <div className="text-[10px] font-black uppercase text-slate-500">Convs</div>
              <div className="text-base font-black font-mono text-emerald-400 mt-0.5">{totals.conversions || 0}</div>
            </div>
            <div className="p-2.5 bg-slate-950/80 rounded-xl border border-slate-800/80">
              <div className="text-[10px] font-black uppercase text-slate-500">Revenue</div>
              <div className="text-base font-black font-mono text-emerald-400 mt-0.5">${Number(totals.revenue || 0).toFixed(2)}</div>
            </div>
            <div className="p-2.5 bg-slate-950/80 rounded-xl border border-slate-800/80">
              <div className="text-[10px] font-black uppercase text-slate-500">CPC (Spend÷Visits)</div>
              <div className="text-base font-black font-mono text-slate-200 mt-0.5">${Number(totals.cpc || 0).toFixed(3)}</div>
            </div>
            <div className="p-2.5 bg-slate-950/80 rounded-xl border border-slate-800/80">
              <div className="text-[10px] font-black uppercase text-slate-500">EPC (Rev÷Visits)</div>
              <div className="text-base font-black font-mono text-slate-200 mt-0.5">${Number(totals.epc || 0).toFixed(3)}</div>
            </div>
            <div className="p-2.5 bg-slate-950/80 rounded-xl border border-slate-800/80">
              <div className="text-[10px] font-black uppercase text-slate-500">ROI</div>
              <div className={`text-base font-black font-mono mt-0.5 ${(totals.roi || 0) >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                {totals.roi !== null && totals.roi !== undefined ? `${Number(totals.roi).toFixed(1)}%` : '—'}
              </div>
            </div>
            <div className="p-2.5 bg-slate-950/80 rounded-xl border border-slate-800/80">
              <div className="text-[10px] font-black uppercase text-slate-500">EPC % of CPC</div>
              <div className="mt-1">{renderEpcPctBadge(totals.epcPctOfCpc, totals.epcVsCpcColor)}</div>
            </div>
          </div>

          {/* Hierarchical Report Table: Campaign -> Angle -> Page -> Slot */}
          <div className="bg-[#11151F] border border-slate-800 rounded-2xl overflow-x-auto shadow-2xl">
            <table className="w-full text-left border-collapse text-xs">
              <thead>
                <tr className="border-b border-slate-800 bg-slate-950/70 text-[10px] font-black uppercase tracking-wider text-slate-400">
                  <th className="py-3.5 px-4">Campaign / Angle / Page / Slot</th>
                  <th className="py-3.5 px-3 text-right">Spend</th>
                  <th className="py-3.5 px-3 text-right">Visits</th>
                  <th className="py-3.5 px-3 text-right">Quiz %</th>
                  <th className="py-3.5 px-3 text-right">Slot Clicks</th>
                  <th className="py-3.5 px-3 text-right">CTR</th>
                  <th className="py-3.5 px-3 text-right">Convs</th>
                  <th className="py-3.5 px-3 text-right">Revenue</th>
                  <th className="py-3.5 px-3 text-right">CPC</th>
                  <th className="py-3.5 px-3 text-right">EPC</th>
                  <th className="py-3.5 px-3 text-right">ROI</th>
                  <th className="py-3.5 px-4 text-right">EPC % of CPC</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/60 font-mono">
                {(reportData.campaigns || []).length === 0 ? (
                  <tr>
                    <td colSpan={12} className="py-10 text-center text-slate-500 font-sans">
                      No paid traffic activity found for the selected date range.
                    </td>
                  </tr>
                ) : (
                  reportData.campaigns.map(camp => {
                    const isCampOpen = !!expandedCampaigns[camp.campaignId];
                    return (
                      <React.Fragment key={camp.campaignId}>
                        {/* Level 1: Campaign Row */}
                        <tr
                          onClick={() => setExpandedCampaigns(p => ({ ...p, [camp.campaignId]: !p[camp.campaignId] }))}
                          className="bg-slate-900/80 hover:bg-slate-800/70 cursor-pointer font-bold text-white"
                        >
                          <td className="py-3 px-4 font-sans flex items-center gap-2">
                            {isCampOpen ? <ChevronDown size={14} className="text-[#02B6FF]" /> : <ChevronRight size={14} className="text-slate-500" />}
                            <span className="px-2 py-0.5 text-[9px] font-black uppercase rounded bg-[#02B6FF]/15 text-[#33D5E6] border border-[#02B6FF]/30">Campaign</span>
                            <span>{camp.campaignName}</span>
                          </td>
                          <td className="py-3 px-3 text-right text-rose-400">${Number(camp.spend || 0).toFixed(2)}</td>
                          <td className="py-3 px-3 text-right">{camp.visits}</td>
                          <td className="py-3 px-3 text-right text-indigo-300">{Number(camp.quizCompletionRate || 0).toFixed(1)}%</td>
                          <td className="py-3 px-3 text-right text-cyan-300">{camp.slotClicks}</td>
                          <td className="py-3 px-3 text-right">{Number(camp.ctr || 0).toFixed(1)}%</td>
                          <td className="py-3 px-3 text-right text-emerald-400">{camp.conversions}</td>
                          <td className="py-3 px-3 text-right text-emerald-400">${Number(camp.revenue || 0).toFixed(2)}</td>
                          <td className="py-3 px-3 text-right">${Number(camp.cpc || 0).toFixed(3)}</td>
                          <td className="py-3 px-3 text-right">${Number(camp.epc || 0).toFixed(3)}</td>
                          <td className={`py-3 px-3 text-right ${(camp.roi || 0) >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                            {camp.roi !== null && camp.roi !== undefined ? `${Number(camp.roi).toFixed(1)}%` : '—'}
                          </td>
                          <td className="py-3 px-4 text-right">{renderEpcPctBadge(camp.epcPctOfCpc, camp.epcVsCpcColor)}</td>
                        </tr>

                        {/* Level 2: Angle Rows */}
                        {isCampOpen && (camp.angles || []).map(ang => {
                          const angKey = `${camp.campaignId}__${ang.angleId}`;
                          const isAngOpen = expandedAngles[angKey] !== false;
                          return (
                            <React.Fragment key={angKey}>
                              <tr
                                onClick={() => setExpandedAngles(p => ({ ...p, [angKey]: !isAngOpen }))}
                                className="bg-slate-950/60 hover:bg-slate-900/50 cursor-pointer text-slate-200"
                              >
                                <td className="py-2.5 pl-9 pr-4 font-sans flex items-center gap-2">
                                  {isAngOpen ? <ChevronDown size={13} className="text-indigo-400" /> : <ChevronRight size={13} className="text-slate-500" />}
                                  <span className="px-1.5 py-0.5 text-[9px] font-black uppercase rounded bg-indigo-500/15 text-indigo-300 border border-indigo-500/30">Angle</span>
                                  <span>{ang.angleName}</span>
                                </td>
                                <td className="py-2.5 px-3 text-right text-slate-400">${Number(ang.spend || 0).toFixed(2)}</td>
                                <td className="py-2.5 px-3 text-right">{ang.visits}</td>
                                <td className="py-2.5 px-3 text-right text-indigo-300">{Number(ang.quizCompletionRate || 0).toFixed(1)}%</td>
                                <td className="py-2.5 px-3 text-right text-cyan-300">{ang.slotClicks}</td>
                                <td className="py-2.5 px-3 text-right">{Number(ang.ctr || 0).toFixed(1)}%</td>
                                <td className="py-2.5 px-3 text-right text-emerald-400">{ang.conversions}</td>
                                <td className="py-2.5 px-3 text-right text-emerald-400">${Number(ang.revenue || 0).toFixed(2)}</td>
                                <td className="py-2.5 px-3 text-right">${Number(ang.cpc || 0).toFixed(3)}</td>
                                <td className="py-2.5 px-3 text-right">${Number(ang.epc || 0).toFixed(3)}</td>
                                <td className="py-2.5 px-3 text-right">
                                  {ang.roi !== null && ang.roi !== undefined ? `${Number(ang.roi).toFixed(1)}%` : '—'}
                                </td>
                                <td className="py-2.5 px-4 text-right">{renderEpcPctBadge(ang.epcPctOfCpc, ang.epcVsCpcColor)}</td>
                              </tr>

                              {/* Level 3: Page Rows */}
                              {isAngOpen && (ang.pages || []).map(pg => {
                                const pgKey = `${angKey}__${pg.pageId}`;
                                const isPgOpen = expandedPages[pgKey] !== false;
                                return (
                                  <React.Fragment key={pgKey}>
                                    <tr
                                      onClick={() => setExpandedPages(p => ({ ...p, [pgKey]: !isPgOpen }))}
                                      className="bg-slate-950/90 hover:bg-slate-900/40 cursor-pointer text-slate-300"
                                    >
                                      <td className="py-2 pl-14 pr-4 font-sans flex items-center gap-2">
                                        {isPgOpen ? <ChevronDown size={12} className="text-purple-400" /> : <ChevronRight size={12} className="text-slate-500" />}
                                        <span className="px-1.5 py-0.5 text-[9px] font-black uppercase rounded bg-purple-500/15 text-purple-300 border border-purple-500/30">Page</span>
                                        <span>{pg.pageName}</span>
                                      </td>
                                      <td className="py-2 px-3 text-right text-slate-500">${Number(pg.spend || 0).toFixed(2)}</td>
                                      <td className="py-2 px-3 text-right">{pg.visits}</td>
                                      <td className="py-2 px-3 text-right text-indigo-300">{Number(pg.quizCompletionRate || 0).toFixed(1)}%</td>
                                      <td className="py-2 px-3 text-right text-cyan-300">{pg.slotClicks}</td>
                                      <td className="py-2 px-3 text-right">{Number(pg.ctr || 0).toFixed(1)}%</td>
                                      <td className="py-2 px-3 text-right text-emerald-400">{pg.conversions}</td>
                                      <td className="py-2 px-3 text-right text-emerald-400">${Number(pg.revenue || 0).toFixed(2)}</td>
                                      <td className="py-2 px-3 text-right">${Number(pg.cpc || 0).toFixed(3)}</td>
                                      <td className="py-2 px-3 text-right">${Number(pg.epc || 0).toFixed(3)}</td>
                                      <td className="py-2 px-3 text-right">
                                        {pg.roi !== null && pg.roi !== undefined ? `${Number(pg.roi).toFixed(1)}%` : '—'}
                                      </td>
                                      <td className="py-2 px-4 text-right">{renderEpcPctBadge(pg.epcPctOfCpc, pg.epcVsCpcColor)}</td>
                                    </tr>

                                    {/* Level 4: Slot Rows */}
                                    {isPgOpen && (pg.slots || []).map(sl => (
                                      <tr key={`${pgKey}__${sl.slot}`} className="bg-black/40 text-slate-400 text-[11px]">
                                        <td className="py-1.5 pl-20 pr-4 font-sans flex items-center gap-2">
                                          <span className="px-1.5 py-0.5 text-[9px] font-mono font-bold rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
                                            Slot #{sl.slot}
                                          </span>
                                          {sl.offerId && <span className="text-[10px] text-slate-500">({sl.offerId})</span>}
                                        </td>
                                        <td className="py-1.5 px-3 text-right text-slate-600">—</td>
                                        <td className="py-1.5 px-3 text-right">{sl.visits}</td>
                                        <td className="py-1.5 px-3 text-right text-slate-600">—</td>
                                        <td className="py-1.5 px-3 text-right text-cyan-300 font-bold">{sl.slotClicks}</td>
                                        <td className="py-1.5 px-3 text-right">{Number(sl.ctr || 0).toFixed(1)}%</td>
                                        <td className="py-1.5 px-3 text-right text-emerald-400 font-bold">{sl.conversions}</td>
                                        <td className="py-1.5 px-3 text-right text-emerald-400 font-bold">${Number(sl.revenue || 0).toFixed(2)}</td>
                                        <td className="py-1.5 px-3 text-right text-slate-600">—</td>
                                        <td className="py-1.5 px-3 text-right">${Number(sl.epc || 0).toFixed(3)}</td>
                                        <td className="py-1.5 px-3 text-right text-slate-600">—</td>
                                        <td className="py-1.5 px-4 text-right text-slate-600">—</td>
                                      </tr>
                                    ))}
                                  </React.Fragment>
                                );
                              })}
                            </React.Fragment>
                          );
                        })}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════
          TAB 2: STEP 7 — DAILY SPEND ENTRY
      ═══════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'spend' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Spend Entry Form */}
          <form onSubmit={handleSaveSpend} className="bg-[#11151F] border border-slate-800 rounded-2xl p-6 space-y-4 h-fit">
            <div>
              <h3 className="text-sm font-black uppercase tracking-wider text-white flex items-center gap-2">
                <DollarSign size={16} className="text-[#02B6FF]" /> Daily Ad Spend Entry
              </h3>
              <p className="text-xs text-slate-400 mt-1">
                One row per campaign per day. Saving an existing date + campaign updates that row in place.
              </p>
            </div>

            <div>
              <label className="block text-[10px] font-black uppercase tracking-wider text-slate-400 mb-1.5">Date</label>
              <input
                type="date"
                value={spendForm.date}
                onChange={e => setSpendForm(p => ({ ...p, date: e.target.value }))}
                required
                className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3.5 py-2.5 text-xs text-white font-mono"
              />
            </div>

            <div>
              <label className="block text-[10px] font-black uppercase tracking-wider text-slate-400 mb-1.5">Traffic Source</label>
              <select
                value={spendForm.source}
                onChange={e => setSpendForm(p => ({ ...p, source: e.target.value }))}
                className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3.5 py-2.5 text-xs text-white font-bold"
              >
                {TRAFFIC_SOURCE_OPTIONS.map(s => (
                  <option key={s.id} value={s.id}>{s.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-[10px] font-black uppercase tracking-wider text-slate-400 mb-1.5">Campaign</label>
              <select
                value={spendForm.campaignId}
                onChange={e => setSpendForm(p => ({ ...p, campaignId: e.target.value }))}
                required
                className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3.5 py-2.5 text-xs text-white font-bold"
              >
                <option value="">— Select Campaign —</option>
                {campaigns.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-[10px] font-black uppercase tracking-wider text-slate-400 mb-1.5">Spend Amount ($ USD)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={spendForm.amount}
                onChange={e => setSpendForm(p => ({ ...p, amount: e.target.value }))}
                placeholder="e.g. 45.50"
                required
                className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3.5 py-2.5 text-xs text-white font-mono"
              />
            </div>

            <button
              type="submit"
              disabled={savingSpend}
              className="w-full py-3 bg-[#02B6FF] hover:bg-[#33D5E6] text-slate-950 font-black text-xs uppercase tracking-wider rounded-xl transition-all cursor-pointer flex items-center justify-center gap-2"
            >
              <Save size={14} />
              <span>{savingSpend ? 'Saving...' : 'Save Daily Spend'}</span>
            </button>
          </form>

          {/* Spend History Table */}
          <div className="lg:col-span-2 bg-[#11151F] border border-slate-800 rounded-2xl p-6 space-y-4">
            <h3 className="text-sm font-black uppercase tracking-wider text-white">Recorded Daily Spend Rows</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse text-xs">
                <thead>
                  <tr className="border-b border-slate-800 text-[10px] font-black uppercase text-slate-400">
                    <th className="py-2.5 px-3">Date</th>
                    <th className="py-2.5 px-3">Source</th>
                    <th className="py-2.5 px-3">Campaign</th>
                    <th className="py-2.5 px-3 text-right">Amount</th>
                    <th className="py-2.5 px-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/60">
                  {spendRows.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="py-8 text-center text-slate-500">No spend entries recorded yet.</td>
                    </tr>
                  ) : (
                    spendRows.map(row => (
                      <tr key={row.id} className="hover:bg-slate-900/50">
                        <td className="py-2.5 px-3 font-mono text-white">{row.date}</td>
                        <td className="py-2.5 px-3 uppercase font-bold text-indigo-300">{row.source}</td>
                        <td className="py-2.5 px-3 text-slate-200">{row.campaignName || row.campaignId}</td>
                        <td className="py-2.5 px-3 text-right font-mono font-bold text-rose-400">${Number(row.amount || 0).toFixed(2)}</td>
                        <td className="py-2.5 px-3 text-right flex items-center justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => handleEditSpendRow(row)}
                            className="px-2.5 py-1 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-lg text-[11px] font-bold flex items-center gap-1 cursor-pointer"
                          >
                            <Edit3 size={11} /> Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteSpendRow(row.id)}
                            className="p-1.5 bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 rounded-lg cursor-pointer"
                          >
                            <Trash2 size={12} />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════
          TAB 3: STEP 5 — CUSTOM HTML PAGE (OPTION A)
      ═══════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'custom-html' && (
        <div className="space-y-6">
          {/* Security & Custom Domain Callout */}
          <div className="p-4 bg-emerald-950/30 border border-emerald-500/30 rounded-2xl flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <ShieldCheck size={18} className="text-emerald-400 shrink-0 mt-0.5" />
              <div>
                <div className="text-xs font-black uppercase tracking-wider text-emerald-300">
                  Option A: Custom HTML Sandbox Security Active
                </div>
                <p className="text-xs text-slate-300 mt-0.5">
                  Custom HTML pages are served <strong className="text-white">strictly on your custom domains</strong> (e.g. <code className="text-emerald-300">{activeCustomDomain}</code>) and blocked (<code className="text-rose-300">403</code>) on <code className="text-slate-400">affiliateflow.ai</code> so pasted scripts can never read app sessions or cookies.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-mono text-xs text-indigo-300 bg-slate-950 px-3 py-1.5 rounded-xl border border-slate-800">
                {customHtmlLiveUrl}
              </span>
              <button
                type="button"
                onClick={() => copyText(customHtmlLiveUrl, 'html_live_url')}
                className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 cursor-pointer"
              >
                {copiedKey === 'html_live_url' ? <Check size={13} className="text-emerald-400" /> : <Copy size={13} />}
                <span>Copy Page URL</span>
              </button>
            </div>
          </div>

          {/* Warning if no {{offer:N}} placeholder found */}
          {!hasLocalOfferPlaceholders && (
            <div className="p-4 bg-amber-500/15 border border-amber-500/40 rounded-2xl flex items-start gap-3 text-amber-200 text-xs">
              <AlertTriangle size={16} className="text-amber-400 shrink-0 mt-0.5" />
              <div>
                <strong className="font-black uppercase tracking-wider block">Warning: No Offer Placeholders Detected</strong>
                Include <code className="text-white font-mono">{'{{offer:1}}'}</code>, <code className="text-white font-mono">{'{{offer:2}}'}</code>, or <code className="text-white font-mono">{'data-afl-slot="1"'}</code> on your CTA links so AffiliateFlow can replace them at serve time with tracked <code className="text-white font-mono">/r/</code> links and record slot clicks.
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Left Column: Config + Code Editor */}
            <div className="lg:col-span-7 bg-[#11151F] border border-slate-800 rounded-2xl p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block text-[10px] font-black uppercase text-slate-400 mb-1">Campaign</label>
                  <select
                    value={htmlCampaignId}
                    onChange={e => setHtmlCampaignId(e.target.value)}
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs text-white font-bold"
                  >
                    {campaigns.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-black uppercase text-slate-400 mb-1">Page Slug on {activeCustomDomain}</label>
                  <input
                    type="text"
                    value={htmlPageSlug}
                    onChange={e => setHtmlPageSlug(e.target.value)}
                    placeholder={effectiveHtmlSlug}
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs text-white font-mono"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-black uppercase text-slate-400 mb-1">Angle ID</label>
                  <input
                    type="text"
                    value={htmlAngleId}
                    onChange={e => setHtmlAngleId(e.target.value)}
                    placeholder="angle-1"
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs text-white font-mono"
                  />
                </div>
              </div>

              {/* Noindex Toggle + Size Indicator */}
              <div className="flex flex-wrap items-center justify-between gap-4 p-3 bg-slate-950 border border-slate-800 rounded-xl">
                <label className="flex items-center gap-2.5 cursor-pointer text-xs font-bold text-white">
                  <input
                    type="checkbox"
                    checked={htmlNoindex}
                    onChange={e => setHtmlNoindex(e.target.checked)}
                    className="rounded border-slate-700"
                  />
                  <span>Inject <code className="text-indigo-300">noindex, nofollow</code> meta &amp; header (Recommended default ON)</span>
                </label>
                <span className={`text-[11px] font-mono ${htmlByteSize > 1048576 ? 'text-rose-400 font-bold' : 'text-slate-400'}`}>
                  {(htmlByteSize / 1024).toFixed(1)} KB / 1,024 KB limit
                </span>
              </div>

              {/* Code Box */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-[10px] font-black uppercase tracking-wider text-slate-400">
                    Full HTML Document (Use {'{{offer:1}}'}, {'{{offer:2}}'} or data-afl-slot="1")
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowHtmlPreview(!showHtmlPreview)}
                    className="text-xs font-bold text-[#33D5E6] flex items-center gap-1 cursor-pointer"
                  >
                    <Eye size={13} /> {showHtmlPreview ? 'Hide Live Preview' : 'Show Live Preview'}
                  </button>
                </div>
                <textarea
                  rows={16}
                  value={htmlCode}
                  onChange={e => setHtmlCode(e.target.value)}
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3.5 font-mono text-xs text-emerald-300 focus:outline-none focus:border-[#02B6FF]"
                />
              </div>

              <div className="flex items-center justify-between gap-4">
                <button
                  type="button"
                  onClick={handleSaveCustomHtml}
                  disabled={savingHtml}
                  className="px-6 py-3 bg-[#02B6FF] hover:bg-[#33D5E6] text-slate-950 font-black text-xs uppercase tracking-wider rounded-xl cursor-pointer flex items-center gap-2"
                >
                  <Save size={14} />
                  <span>{savingHtml ? 'Saving...' : 'Save Custom HTML & Version'}</span>
                </button>

                {/* Version History Dropdown (Last 10 Saves) */}
                {htmlVersions.length > 0 && (
                  <div className="flex items-center gap-2">
                    <History size={14} className="text-slate-400" />
                    <select
                      onChange={e => {
                        const v = htmlVersions.find(ver => ver.versionId === e.target.value);
                        if (v && v.html) {
                          setHtmlCode(v.html);
                          showToast(`Restored version from ${new Date(v.savedAt).toLocaleTimeString()}`);
                        }
                      }}
                      defaultValue=""
                      className="bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs text-slate-200 font-mono"
                    >
                      <option value="" disabled>Restore Version ({htmlVersions.length}/10 saved)...</option>
                      {htmlVersions.map((ver, idx) => (
                        <option key={ver.versionId} value={ver.versionId}>
                          #{idx + 1} — {new Date(ver.savedAt).toLocaleString()} ({(ver.sizeBytes / 1024).toFixed(1)} KB)
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>

            {/* Right Column: Live Sandboxed Preview */}
            <div className="lg:col-span-5 bg-[#11151F] border border-slate-800 rounded-2xl p-4 flex flex-col space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-black uppercase tracking-wider text-slate-300 flex items-center gap-1.5">
                  <Eye size={14} className="text-[#02B6FF]" /> Live Sandboxed Preview
                </span>
                <span className="text-[10px] font-mono text-slate-500">sandbox="allow-same-origin"</span>
              </div>
              <div className="flex-1 bg-white rounded-xl overflow-hidden border border-slate-800 min-h-[420px]">
                {showHtmlPreview && (
                  <iframe
                    title="Custom HTML Live Preview"
                    sandbox=""
                    srcDoc={htmlCode}
                    className="w-full h-[460px] border-0"
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════
          TAB 4: STEP 6 — EXTERNAL PAGES (OPTION B)
      ═══════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'external-pages' && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Registration Form */}
          <form onSubmit={handleSaveExternalPage} className="lg:col-span-5 bg-[#11151F] border border-slate-800 rounded-2xl p-6 space-y-4 h-fit">
            <div>
              <h3 className="text-sm font-black uppercase tracking-wider text-white flex items-center gap-2">
                <Globe size={16} className="text-[#02B6FF]" /> Register External Page (Option B)
              </h3>
              <p className="text-xs text-slate-400 mt-1">
                Track pages hosted on your own site (e.g. <code className="text-indigo-300">moneyxps.com</code>) with AffiliateFlow's <code className="text-indigo-300">t.js</code> beacon and slot links.
              </p>
            </div>

            <div>
              <label className="block text-[10px] font-black uppercase text-slate-400 mb-1">Page Name</label>
              <input
                type="text"
                value={extForm.name}
                onChange={e => setExtForm(p => ({ ...p, name: e.target.value }))}
                placeholder="e.g. MoneyXPS Debt Relief Quiz"
                required
                className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3.5 py-2.5 text-xs text-white font-bold"
              />
            </div>

            <div>
              <label className="block text-[10px] font-black uppercase text-slate-400 mb-1">External Page URL (CORS Origin)</label>
              <input
                type="url"
                value={extForm.url}
                onChange={e => setExtForm(p => ({ ...p, url: e.target.value }))}
                placeholder="https://moneyxps.com/debt-quiz"
                required
                className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3.5 py-2.5 text-xs text-white font-mono"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] font-black uppercase text-slate-400 mb-1">Campaign</label>
                <select
                  value={extForm.campaignId}
                  onChange={e => setExtForm(p => ({ ...p, campaignId: e.target.value }))}
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2.5 text-xs text-white font-bold"
                >
                  {campaigns.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-black uppercase text-slate-400 mb-1">Angle</label>
                <input
                  type="text"
                  value={extForm.angle}
                  onChange={e => setExtForm(p => ({ ...p, angle: e.target.value }))}
                  placeholder="angle-1"
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2.5 text-xs text-white font-mono"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] font-black uppercase text-slate-400 mb-1">Traffic Source</label>
                <select
                  value={extForm.source}
                  onChange={e => setExtForm(p => ({ ...p, source: e.target.value }))}
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2.5 text-xs text-white font-bold"
                >
                  {TRAFFIC_SOURCE_OPTIONS.map(s => (
                    <option key={s.id} value={s.id}>{s.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-black uppercase text-slate-400 mb-1">Tracking Custom Domain</label>
                <input
                  type="text"
                  value={extForm.customDomain}
                  onChange={e => setExtForm(p => ({ ...p, customDomain: e.target.value }))}
                  placeholder="go.moneyxps.com"
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2.5 text-xs text-white font-mono"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={savingExtPage}
              className="w-full py-3 bg-[#02B6FF] hover:bg-[#33D5E6] text-slate-950 font-black text-xs uppercase tracking-wider rounded-xl cursor-pointer flex items-center justify-center gap-2"
            >
              <Plus size={14} />
              <span>{savingExtPage ? 'Registering...' : 'Register External Page & Generate Snippet'}</span>
            </button>
          </form>

          {/* Snippet, Slot Links & Ad Destination URL Output */}
          <div className="lg:col-span-7 space-y-4">
            {selectedExtPage ? (
              <div className="bg-[#11151F] border border-slate-800 rounded-2xl p-6 space-y-5">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-black text-white uppercase tracking-wider">{selectedExtPage.name}</h4>
                    <p className="text-xs font-mono text-slate-400 mt-0.5">{selectedExtPage.url}</p>
                  </div>
                  <span className="px-2.5 py-1 rounded-full text-[10px] font-mono font-bold bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
                    Page ID: {selectedExtPage.id}
                  </span>
                </div>

                {/* (a) Tracking Snippet */}
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <label className="text-[10px] font-black uppercase tracking-wider text-slate-300">
                      (a) Tracking Snippet — Paste in &lt;head&gt; of {selectedExtPage.url}
                    </label>
                    <button
                      type="button"
                      onClick={() => copyText(selectedExtPage.trackingSnippet, 'ext_snippet')}
                      className="px-2.5 py-1 bg-slate-800 hover:bg-slate-700 text-xs font-bold text-[#33D5E6] rounded-lg flex items-center gap-1 cursor-pointer"
                    >
                      {copiedKey === 'ext_snippet' ? <Check size={12} /> : <Copy size={12} />}
                      <span>{copiedKey === 'ext_snippet' ? 'Copied!' : 'Copy Snippet'}</span>
                    </button>
                  </div>
                  <pre className="p-3 bg-slate-950 border border-slate-800 rounded-xl font-mono text-xs text-emerald-300 overflow-x-auto">
                    {selectedExtPage.trackingSnippet}
                  </pre>
                  <p className="text-[11px] text-slate-400">
                    Optional event trigger: <code className="text-indigo-300 font-mono">window.afl?.track('quiz_complete')</code> or <code className="text-indigo-300 font-mono">window.afl?.track('email_capture')</code>
                  </p>
                </div>

                {/* (b) Slot Tracking Links */}
                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase tracking-wider text-slate-300 block">
                    (b) Offer Slot Tracking Links (<code className="text-indigo-300">t.js</code> automatically appends visit ID <code className="text-indigo-300">&amp;v=...</code>)
                  </label>
                  {(selectedExtPage.slotLinks || []).map(sl => (
                    <div key={sl.slot} className="p-3 bg-slate-950 border border-slate-800 rounded-xl flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <span className="text-[10px] font-black uppercase text-emerald-400 block">Slot #{sl.slot}</span>
                        <code className="text-xs font-mono text-indigo-300 break-all">{sl.trackingLink}</code>
                      </div>
                      <button
                        type="button"
                        onClick={() => copyText(sl.trackingLink, `slot_${sl.slot}`)}
                        className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-xs font-bold text-white rounded-lg shrink-0 cursor-pointer"
                      >
                        {copiedKey === `slot_${sl.slot}` ? 'Copied!' : 'Copy'}
                      </button>
                    </div>
                  ))}
                </div>

                {/* (c) Ad Destination URL Template */}
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <label className="text-[10px] font-black uppercase tracking-wider text-slate-300">
                      (c) Ad Destination URL Template ({selectedExtPage.source?.toUpperCase()})
                    </label>
                    <button
                      type="button"
                      onClick={() => copyText(selectedExtPage.adDestinationUrlTemplate, 'ext_ad_url')}
                      className="px-2.5 py-1 bg-slate-800 hover:bg-slate-700 text-xs font-bold text-[#33D5E6] rounded-lg flex items-center gap-1 cursor-pointer"
                    >
                      {copiedKey === 'ext_ad_url' ? <Check size={12} /> : <Copy size={12} />}
                      <span>{copiedKey === 'ext_ad_url' ? 'Copied!' : 'Copy Ad URL'}</span>
                    </button>
                  </div>
                  <div className="p-3 bg-slate-950 border border-slate-800 rounded-xl font-mono text-xs text-amber-300 break-all">
                    {selectedExtPage.adDestinationUrlTemplate}
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-[#11151F] border border-slate-800 rounded-2xl p-8 text-center text-slate-500 text-xs">
                Register an external page on the left to generate its <code className="text-slate-300">t.js</code> snippet, slot tracking links, and ad destination URL.
              </div>
            )}

            {/* Registered External Pages List */}
            {externalPages.length > 0 && (
              <div className="bg-[#11151F] border border-slate-800 rounded-2xl p-5 space-y-3">
                <h4 className="text-xs font-black uppercase tracking-wider text-slate-400">Registered External Pages</h4>
                <div className="divide-y divide-slate-800/60">
                  {externalPages.map(p => (
                    <div key={p.id} className="py-2.5 flex items-center justify-between gap-3">
                      <button
                        type="button"
                        onClick={() => setSelectedExtPage(p)}
                        className="text-left hover:text-white cursor-pointer"
                      >
                        <div className="text-xs font-bold text-white">{p.name}</div>
                        <div className="text-[11px] font-mono text-slate-400">{p.url} · Source: {p.source}</div>
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteExternalPage(p.id)}
                        className="p-1.5 text-rose-400 hover:bg-rose-500/15 rounded-lg cursor-pointer"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
